import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TomTomSearchResult {
  id: string;
  type: string;
  address: {
    freeformAddress: string;
    municipality?: string;
    countrySubdivision?: string;
    countryCode: string;
  };
  position: {
    lat: number;
    lon: number;
  };
}

export interface TomTomPlaceData {
  searchText: string; // Det brukeren søkte på (f.eks. "Sandvika kulturhus")
  address: string; // Full adresse fra TomTom (f.eks. "Sandvika kulturhus, 1300 Sandvika")
  latitude: number;
  longitude: number;
}

interface TomTomPlacesAutocompleteProps {
  value: string; // Søketekst (vises i feltet)
  onChange: (value: string) => void; // Oppdaterer søketekst
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onPlaceSelect?: (placeData: TomTomPlaceData) => void; // Kalles når bruker velger fra forslag
}

const TomTomPlacesAutocomplete: React.FC<TomTomPlacesAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Søk etter plassering...',
  className,
  disabled = false,
  onPlaceSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<TomTomSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [searchText, setSearchText] = useState(value);
  const [selectedPlaceData, setSelectedPlaceData] = useState<TomTomPlaceData | null>(null);
  const keepFocusRef = useRef(false); // Simple flag: should we keep focus?
  const displayTextRef = useRef<string | null>(null); // Store display text when suggestion is selected
  const hasEverFocusedRef = useRef(false); // Track if input has ever been focused

  const apiKey = process.env.REACT_APP_TOMTOM_API_KEY;

  // Fetch suggestions from TomTom Search API
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!apiKey || !query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // ALWAYS check if input has focus - this runs EVERY time, so it's always accurate
    keepFocusRef.current = document.activeElement === inputRef.current;

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.tomtom.com/search/2/search/${encodeURIComponent(query)}.json?key=${apiKey}&countrySet=NO&limit=5&typeahead=true`
      );

      if (!response.ok) {
        throw new Error(`TomTom API error: ${response.status}`);
      }

      const data = await response.json();
      const results: TomTomSearchResult[] = data.results || [];
      
      // Update state
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSelectedIndex(-1);
      
      // CRITICAL: Restore focus IMMEDIATELY after state update if it should be kept
      if (keepFocusRef.current && inputRef.current) {
        // Use setTimeout(0) to run after React's state update
        setTimeout(() => {
          if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        }, 0);
      }
    } catch (error) {
      console.error('TomTom Search API error:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey]);

  // Sync searchText with value prop
  useEffect(() => {
    setSearchText(value);
    // Reset suggestions when value changes externally (e.g., when entering edit mode)
    // Don't auto-show suggestions unless user explicitly focuses input
    setShowSuggestions(false);
    setSuggestions([]);
  }, [value]);

  // CRITICAL: Restore focus SYNCHRONOUSLY after every render if it should be kept
  useLayoutEffect(() => {
    if (keepFocusRef.current && inputRef.current && document.activeElement !== inputRef.current) {
      const input = inputRef.current;
      const len = input.value.length;
      input.focus();
      input.setSelectionRange(len, len);
    }
  });

  // Debounced search - only fetch if input has been focused
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only fetch suggestions if input has ever been focused (user is actively typing)
    if (hasEverFocusedRef.current && searchText && searchText.length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(searchText);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchText, fetchSuggestions]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    onChange(newValue);
    setSelectedPlaceData(null);
    keepFocusRef.current = true; // User is typing, so input should have focus
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: TomTomSearchResult) => {
    setShowSuggestions(false);
    setSuggestions([]);
    
    const placeData: TomTomPlaceData = {
      searchText: searchText,
      address: suggestion.address.freeformAddress,
      latitude: suggestion.position.lat,
      longitude: suggestion.position.lon,
    };
    
    setSelectedPlaceData(placeData);
    
    // Update input to show "searchText - address" for display
    const displayText = `${searchText} - ${suggestion.address.freeformAddress}`;
    displayTextRef.current = displayText;
    setSearchText(displayText);
    // Note: onChange is called with displayText, but parent should save searchText and address separately
    
    inputRef.current?.focus();
    
    if (onPlaceSelect) {
      onPlaceSelect(placeData);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format suggestion display text
  const formatSuggestion = (suggestion: TomTomSearchResult): string => {
    const addr = suggestion.address;
    let text = addr.freeformAddress;
    if (addr.municipality) {
      text += `, ${addr.municipality}`;
    }
    return text;
  };

  if (!apiKey) {
    return (
      <div className="space-y-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
            'bg-slate-50/50 focus:bg-white',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className
          )}
        />
        <p className="text-xs text-muted-foreground">
          TomTom API key ikke konfigurert - bruker vanlig tekstinput
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            keepFocusRef.current = true;
            hasEverFocusedRef.current = true; // Mark that input has been focused
            // Only show suggestions if user explicitly focuses the input
            // Don't auto-show when component mounts or when edit mode is activated
            if (suggestions.length > 0 && document.activeElement === inputRef.current) {
              setShowSuggestions(true);
            }
          }}
          onBlur={(e) => {
            // Delay blur check to allow clicks on suggestions
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!containerRef.current?.contains(relatedTarget)) {
              setTimeout(() => {
                if (!containerRef.current?.contains(document.activeElement)) {
                  keepFocusRef.current = false;
                }
              }, 150);
            }
          }}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(
            'w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-lg',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
            'bg-slate-50/50 focus:bg-white',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className
          )}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {searchText && !isLoading && (
          <button
            type="button"
            onClick={() => {
              setSearchText('');
              onChange('');
              setShowSuggestions(false);
              setSelectedPlaceData(null);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent input blur
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur
              }}
              className={cn(
                'w-full px-4 py-3 text-left text-sm hover:bg-slate-50 transition-colors',
                'flex items-start gap-2',
                index === selectedIndex && 'bg-slate-100',
                index === 0 && 'rounded-t-lg',
                index === suggestions.length - 1 && 'rounded-b-lg'
              )}
            >
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">
                  {formatSuggestion(suggestion)}
                </p>
                {suggestion.address.municipality && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {suggestion.address.municipality}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TomTomPlacesAutocomplete;
